import React, { useEffect, useMemo, useRef } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore, {
  Navigation,
  Pagination,
  Autoplay,
  EffectFade,
} from 'swiper'
import Slide from './slide'
import { sendTeaCommEvent } from 'lib/tea-analyze'
import { BANNER } from './constant/module-id'
import { useInView } from 'react-intersection-observer'

SwiperCore.use([Navigation, Pagination, Autoplay, EffectFade])

interface IListItem {
  group_id: string
  img_url: string
  title: string
  is_ugc: boolean
}

export type PicList = {
  pic_type: 1101 | 1102 | 1103
  pic_list: IListItem[]
}[]

interface IBanner {
  className?: string
  focus_pic: PicList
}

const Banner = (props: IBanner) => {
  const { className, focus_pic: list = [] } = props
  const [ref, inView] = useInView({
    threshold: 0,
  })
  const inViewRef = useRef(false)
  useEffect(() => {
    inViewRef.current = inView
  }, [inView])
  const reformedList = useMemo(() => {
    if (list.length === 0) {
      return []
    }
    const rl = []
    const min = list.reduce((acc, cur) => {
      if (cur?.pic_list?.length < acc) {
        return cur.pic_list.length
      } else {
        return acc
      }
    }, list?.[0]?.pic_list?.length || 0)
    for (let i = 0; i < min; i++) {
      const item = []
      for (let j = 0; j < list.length; j++) {
        item.push({
          type: list[j]?.pic_type,
          gid: list[j]?.pic_list?.[i]?.group_id,
          img_url: list[j]?.pic_list?.[i]?.img_url,
          title: list[j]?.pic_list?.[i]?.title,
          is_ugc: list[j]?.pic_list?.[i]?.is_ugc,
        })
      }
      item.sort((a, b) => a.type - b.type)
      rl.push(item)
    }
    return rl
  }, [list])

  return (
    <div
      className={`banner tw-col-span-20
        2xl:tw-col-span-24
      `}
      ref={ref}
      id="banner"
    >
      {reformedList.length > 1 ? (
        <Swiper
          navigation={{
            nextEl: '.banner-nav-next',
            prevEl: '.banner-nav-pre',
          }}
          className={`${className} swiper-class`}
          preventClicks={false}
          allowTouchMove={false}
          loop
          autoHeight
          autoplay={{
            disableOnInteraction: false,
            delay: 5000,
          }}
          effect="fade"
          observeParents={true}
          observer={true}
          resizeObserver={true}
          pagination={{
            el: '.banner-swiper-pagination',
            type: 'bullets',
            bulletActiveClass: 'banner-active-bullet',
            bulletClass: 'banner-bullet',
            clickable: true,
          }}
          onSlideChange={(swiper) => {
            if (inViewRef.current) {
              sendTeaCommEvent('show_event', {
                event_id: 'pic_banner_module_item',
                rank: swiper.realIndex,
                module_id: BANNER,
              })
            }
          }}
        >
          {reformedList.map((imgs, index) => (
            <SwiperSlide key={index}>
              <Slide
                items={imgs}
                className="banner-cover"
                onClick={(pic_size, target_url, item_rank) => {
                  sendTeaCommEvent('clk_event', {
                    event_id: 'pic_banner_module_item_pic',
                    rank: index,
                    pic_size,
                    target_url,
                    item_rank,
                  })
                }}
              />
            </SwiperSlide>
          ))}
          <div
            className="banner-nav-pre swiper-nav-pre tw-inline-block"
            onClick={() => {
              sendTeaCommEvent('clk_event', {
                event_id: 'pic_banner_module_change',
                module_id: BANNER,
              })
            }}
          />
          <div
            className="banner-nav-next swiper-nav-next tw-inline-block"
            onClick={() => {
              sendTeaCommEvent('clk_event', {
                event_id: 'pic_banner_module_change',
                module_id: BANNER,
              })
            }}
          />
        </Swiper>
      ) : (
        <Slide
          items={reformedList[0]}
          className={`${className} banner-cover`}
          onClick={(pic_size, target_url, item_rank) => {
            sendTeaCommEvent('clk_event', {
              event_id: 'pic_banner_module_item_pic',
              rank: 0,
              pic_size,
              target_url,
              item_rank,
            })
          }}
        />
      )}
      {reformedList.length > 1 && (
        <div
          className="banner-swiper-pagination tw-bottom-8 tw-flex 2xl:tw--bottom-10"
          onClick={(eve) => {
            if (eve.target !== eve.currentTarget) {
              let i
              for (i = 0; i < eve.currentTarget.children.length; i++) {
                if (eve.currentTarget.children[i] === eve.target) {
                  break
                }
              }
              sendTeaCommEvent('clk_event', {
                event_id: 'pic_banner_module_pagination',
                module_id: BANNER,
                rank: i,
              })
            }
          }}
        />
      )}

      <style jsx>
        {`
          .banner-swiper-pagination {
            @apply tw-w-100 tw-absolute tw-h-4 tw-right-4 tw-z-1 tw-justify-end tw-items-end;
            left: auto;
          }
          :global(.banner-swiper-pagination .banner-bullet) {
            @apply tw-h-3 tw-w-12 tw-mr-4 tw-cursor-pointer tw-bg-video-gray;
          }
          :global(.banner-swiper-pagination .banner-bullet:hover) {
            @apply tw-h-5;
          }
          :global(.banner-swiper-pagination .banner-active-bullet) {
            @apply tw-w-16 tw-h-4 tw-bg-common-black;
          }
        `}
      </style>
    </div>
  )
}

export default Banner
